import { V3FiltersRequest, V4SearchRequest } from '@sbercloud/typings-docs-facade-search';
import { useState } from 'react';

import { FEEDBACK_FORM_FALLBACK_VALUES } from '@/constant/forms';
import { BACKEND_ENDPOINTS } from '@/constant/network';
import { MIN_SEARCH_PHRASE_FOR_SEARCH } from '@/constant/serp';
import { getGlobalArgs } from '@/root/global';
import { createFeedbackHandler } from '@/root/src/services/feedback';
import { FeedbackFormValues, IsLiked, SearchBaseRequest, UseSERPFeedbackPayload } from '@/types/feedback';
import { CategoryDataItem, GetSERPFiltersRes, GetSERPFiltersResDocs, SearchArticlesRes } from '@/types/serp';
import { equals, isNil, mergeRight } from '@/utils/common';
import { isClientSide } from '@/utils/next';
import { getFullCurrentPath } from '@/utils/routes';

import { useQuery } from './query';

export const useSERPFilters = (): { isLoading: boolean; docsData: GetSERPFiltersResDocs | null } => {
  const { isLoading, response } = useQuery<GetSERPFiltersRes, V3FiltersRequest>({
    query: BACKEND_ENDPOINTS.serpFilters,
    args: {
      sources: [
        {
          filters: ['platforms', 'products'],
          hierarchy: {
            platforms: 'products',
          },
          name: 'docs',
        },
      ],
    },
  });

  return { isLoading, docsData: response?.data?.sources?.docs || null };
};

export const useSearchArticles = (
  searchData: {
    platforms: string[];
    phrase: string;
    products: string[];
    withTranslate?: boolean;
  },
  skipRequest?: boolean,
): { isLoading: boolean; articles: CategoryDataItem[] | null; searchRequestID: string } => {
  const withTranslate = Boolean(searchData.withTranslate);

  const cleanPhrase = searchData.phrase.trim();
  const { isLoading, response } = useQuery<SearchArticlesRes, V4SearchRequest>({
    query: BACKEND_ENDPOINTS.searchArticles,
    skipReq: skipRequest || cleanPhrase.length < MIN_SEARCH_PHRASE_FOR_SEARCH,
    deps: [searchData],
    args: {
      commonParams: {
        limit: ['100'],
        ow: ['true'],
        use_ranking: ['true'],
        phrase: [cleanPhrase],
        platforms: searchData.platforms,
        products: searchData.products,
      },
      requestContext: {
        retainURL: isClientSide() ? getFullCurrentPath(window.location) : '',
      },
      sources: [{ withTranslate, names: ['docs'].concat(withTranslate ? ['hcdocs'] : []) }],
    },
  });

  return {
    articles: isNil(response) ? response : response?.data.categories?.[0]?.data || [],
    isLoading,
    searchRequestID: response?.data?.searchRequestID || '',
  };
};

export const useSERPFeedback = ({ searchRequestID, shadowInput, extraOnComplete }: UseSERPFeedbackPayload) => {
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [isOpenThanksBlock, setIsOpenThanksBlock] = useState(false);
  const [isOpenFeedbackForm, setIsOpenFeedbackForm] = useState(false);

  const baseBody: SearchBaseRequest = {
    gaClientID: getGlobalArgs('NEXT_PUBLIC_GTM_ID', ''),
    originalUTM: '',
    searchRequestID,
    retainURL: isClientSide() ? getFullCurrentPath(window.location) : '',
    shadowInput: shadowInput || '',
    yaClientID: getGlobalArgs('NEXT_PUBLIC_DOCS_YM_ID', ''),
  };

  const baseSendFeedbackPayload = {
    onStartRequest: () => setIsLoadingFeedback(true),
    onSuccessRequest: () => {
      setIsOpenThanksBlock(true);
    },
    onComplete: (payload: IsLiked) => {
      if (
        payload.isLiked ||
        (Boolean(payload.comment) && !equals(FEEDBACK_FORM_FALLBACK_VALUES.comment, payload.comment))
      ) {
        setIsOpenFeedbackForm(false);
        extraOnComplete?.(setIsOpenThanksBlock);
      }
      setIsLoadingFeedback(false);
    },
    baseBody,
  };

  const chooseScoreFeedbackHandler = createFeedbackHandler(
    mergeRight(baseSendFeedbackPayload, { endpoint: BACKEND_ENDPOINTS.searchFeedback }),
  );

  const submitFeedbackFormHandler = createFeedbackHandler<FeedbackFormValues & IsLiked>(
    mergeRight(baseSendFeedbackPayload, {
      endpoint: BACKEND_ENDPOINTS.searchFeedback,
    }),
  );

  return {
    submitFeedbackFormHandler,
    chooseScoreFeedbackHandler,
    isLoadingFeedback,
    isOpenThanksBlock,
    isOpenFeedbackForm,
    setIsOpenFeedbackForm,
    setIsOpenThanksBlock,
  };
};
